import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BlogPostMeta } from "../../types/blog.model";
import { toYouTubeEmbedUrl } from "../../utils/video.utils";
import { Icon } from "../common/Icon";

interface Props {
  posts: BlogPostMeta[];
  blogSlug: string;
  /** The first post with a video to auto-select on load */
  initialSlug?: string;
}

/**
 * VideoPlaylist — a YouTube embed beside the chapter list.
 *
 * Layout: on desktop the row's height is set by the player (16:9), and the
 * list is absolutely positioned inside its cell so a long playlist scrolls
 * instead of stretching the row and leaving a black gap beside the video.
 * Below lg the two stack, list under player.
 */
export default function VideoPlaylist({ posts, blogSlug, initialSlug }: Props) {
  const navigate = useNavigate();

  const videoPosts = posts.filter((p) => p.videoEmbedUrl);

  const initial = initialSlug
    ? (videoPosts.find((p) => p.slug === initialSlug) ?? videoPosts[0])
    : videoPosts[0];

  const [activePost, setActivePost] = useState<BlogPostMeta | null>(initial ?? null);
  // Only autoplay a track the reader actually chose — never on page load.
  const [autoplay, setAutoplay] = useState(false);

  if (videoPosts.length === 0) return null;

  const embedUrl = activePost?.videoEmbedUrl
    ? toYouTubeEmbedUrl(activePost.videoEmbedUrl)
    : null;

  function handleSelect(item: BlogPostMeta) {
    if (item.videoEmbedUrl) {
      setActivePost(item);
      setAutoplay(true);
    }
    navigate(`/blog/${blogSlug}/${item.slug}`);
  }

  return (
    <section aria-label="Video playlist">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_18rem] rounded-2xl overflow-hidden border border-border bg-surface-container-lowest shadow-[var(--color-card-shadow)]">
        {/* ── Player ─────────────────────────────────────────────────────── */}
        <div className="min-w-0 flex flex-col">
          {embedUrl ? (
            <div className="relative w-full aspect-video bg-black">
              <iframe
                key={activePost?.id} // re-mount iframe on track change
                className="absolute inset-0 h-full w-full"
                src={autoplay ? `${embedUrl}?autoplay=1` : embedUrl}
                title={activePost?.title ?? "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="aspect-video grid place-items-center bg-black text-sm text-white/40">
              No video
            </div>
          )}

          {activePost && (
            <div className="flex items-center gap-3 px-4 py-3 border-t border-border">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-fixed text-primary">
                <Icon name="play-solid" size={14} aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  Now playing
                </span>
                <span className="block truncate text-[15px] font-semibold leading-snug text-foreground">
                  {activePost.title}
                </span>
              </span>
            </div>
          )}
        </div>

        {/* ── Playlist ───────────────────────────────────────────────────── */}
        <div className="relative min-h-0 border-t border-border lg:border-t-0 lg:border-l">
          <div className="flex flex-col lg:absolute lg:inset-0">
            <p className="shrink-0 px-4 pt-3.5 pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Playlist · {posts.length}
            </p>
            <ul className="min-h-0 flex-1 max-h-72 overflow-y-auto lg:max-h-none">
              {posts.map((post, idx) => {
                const isActive = post.slug === activePost?.slug;
                const hasVideo = Boolean(post.videoEmbedUrl);
                return (
                  <li key={post.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(post)}
                      aria-current={isActive ? "true" : undefined}
                      className={`flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors last:border-0 ${
                        isActive
                          ? "bg-primary-fixed"
                          : "text-muted-foreground hover:bg-surface-container hover:text-foreground"
                      }`}
                    >
                      <span className="mt-0.5 shrink-0">
                        <Icon
                          name={hasVideo ? "play-circle" : "file-text"}
                          size={16}
                          className={isActive ? "text-primary" : "text-muted-foreground"}
                          aria-hidden="true"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="mb-0.5 block font-code text-[11px] leading-none text-muted-foreground">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`line-clamp-2 text-sm leading-snug ${
                            isActive ? "font-semibold text-foreground" : ""
                          }`}
                        >
                          {post.title}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
